// ThemeProvider.tsx — Studio M4.5 React context.
//
// Plan ref: 06-00-PLAN §Task 1 step 7;
//           06-RESEARCH-theme-tokens.md §Code Examples §ThemeProvider.
//
// Responsibilities:
//   - Read the user's stored preference (validated against the literal
//     {dark,light,system} set per §Security V5).
//   - Apply data-theme to <html>; the inline pre-hydration script in
//     index.html has already done this on first paint, so the React effect
//     is a no-op on initial mount and only fires on subsequent setTheme.
//   - Listen to OS-level prefers-color-scheme changes ONLY when theme=system.

import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { isValidPref, readStoredPref, type Resolved, type ThemePref } from './preHydration';

const STORAGE_KEY = 'studio.theme';

export interface ThemeContextValue {
  /** What the user picked. */
  theme: ThemePref;
  /** What's actually applied (resolves 'system' against current matchMedia). */
  resolvedTheme: Resolved;
  setTheme: (next: ThemePref) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

function readSystem(): Resolved {
  // Mirrors resolvePreHydrationTheme — Studio is dark-first, so we treat
  // "no preference" as dark and only resolve to light when the OS explicitly
  // signals prefers-color-scheme: light. (The matchMedia(dark) reading would
  // flip "no preference" to light, which contradicts the dark-first contract.)
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'dark';
  }
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function readStored(): ThemePref {
  if (typeof window === 'undefined') return 'system';
  try {
    return readStoredPref(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return 'system';
  }
}

function resolveFor(pref: ThemePref): Resolved {
  return pref === 'system' ? readSystem() : pref;
}

// ?theme=light|dark deep-link override — FIRST in the documented fallback
// chain (tokens.md §6: query → localStorage → system). The pre-hydration
// script honors it, but this provider previously read only localStorage, so
// React reverted the pre-hydration value on mount and ?theme deep links
// (incl. the Lighthouse CI's 14 ?theme= URLs) silently broke
// (milestone-02 Phase 2; audit follow-up). The override applies to the
// INITIAL resolved value only — it is never persisted, and an explicit
// setTheme during the session takes over.
function readQueryOverride(): Resolved | null {
  if (typeof window === 'undefined') return null;
  try {
    const q = new URLSearchParams(window.location.search).get('theme');
    return q === 'light' || q === 'dark' ? q : null;
  } catch {
    return null;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePref>(() => readStored());
  const [resolvedTheme, setResolved] = useState<Resolved>(
    () => readQueryOverride() ?? resolveFor(readStored()),
  );

  // Apply data-theme to <html> whenever resolvedTheme changes. On initial
  // mount this matches the value the inline pre-hydration script already
  // set, so the DOM mutation is a no-op (no flash).
  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
  }, [resolvedTheme]);

  // OS-level system listener — only meaningful when theme pref is 'system'.
  // We listen to prefers-color-scheme: light (rather than dark) so that the
  // initial-resolve and the change-listener share the same source of truth.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e: MediaQueryListEvent) => {
      if (theme === 'system') setResolved(e.matches ? 'light' : 'dark');
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback((next: ThemePref) => {
    if (!isValidPref(next)) return;
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Safari private mode + Lockdown mode throw on setItem. The state
      // mutation is still applied (this session) so the user sees their
      // pick; we just lose persistence.
    }
    setResolved(resolveFor(next));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
